import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    BaseTextFieldProps,
    InputAdornment,
    MenuItem,
    Select,
    TextField,
    Typography,
    Box,
    Collapse,
} from '@mui/material';
import {
    CountryData,
    CountryIso2,
    defaultCountries,
    FlagImage,
    parseCountry,
    ParsedCountry,
    usePhoneInput,
} from 'react-international-phone';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import AuthManager from '../../repository/auth';
import { getCountryNameTranslations } from './country-name-translations';
import 'react-international-phone/style.css';
import usePhoneInputV2Styles from './index.styles';

export type ChangeHandlerParams = {
    phone: string;
    inputValue: string; // formatted phone number
    country: ParsedCountry;
};

export type ChangeHandler = (data: ChangeHandlerParams) => void;

export interface IPhoneInputV2Props extends BaseTextFieldProps {
    value: string;
    defaultCountry: string;
    onChange: ChangeHandler;
    errorText?: string;
    hideErrorText?: boolean;
    defaultMask?: string;
    placeholder?: string;
    label?: string;
    lang?: string;
    isValid?: boolean;
    freezeCountryCode?: boolean;
    /**
     * @deprecated This prop is no longer functional and should be removed.
     * Left-to-right (LTR) layout will be enforced even for right-to-left (RTL) languages, regardless of this parameter's value.
     */
    forceLTR?: boolean;
    /**
     * @param required
     * @description Shows an error when input field is left empty or contains only the country code.
     * @default false
     */
    required?: boolean;
    usePhoneInputHookProps?: {
        prefix?: string;
        charAfterDialCode?: string;
        historySaveDebounceMS?: number;
        disableCountryGuess?: boolean;
        disableDialCodePrefill?: boolean;
        disableDialCodeAndPrefix?: boolean;
        disableFormatting?: boolean;
        inputRef?: React.MutableRefObject<HTMLInputElement | null>;
    };
    flagImageAPIProps?: {
        protocol?: 'http' | 'https';
        disableLazyLoading?: boolean;
    };
    validation?: {
        enable: boolean;
        /**
         * @param
         * A callback function to pass validation status to the parent component.
         */
        callbackFn?: (isValid: boolean) => void;
        /**
         * @param
         * The delay (in milliseconds) to debounce the validation callback function.
         * This is equivalent to the 'wait' parameter in lodash's debounce function.
         */
        debounceWait?: number;
    };

    disableCountrySearch?: boolean;
    countrySearchLabel?: string;
    /**
     * @deprecated This prop is deprecated and it should not be used.
     * If 'required' is true, this will be overridden and an error will be shown
     * when only the country code is entered.
     * @default true
     */
    hideErrorForCountryCode?: boolean;
    /**
     * @param forceErrorText
     * @description Forces the error text to be shown when input value is invalid. Has a higher priority than 'hideErrorText'. Will work only when 'validation.enable' is set to true.
     * @default false
     */
    forceErrorText?: boolean;
}

const PhoneInputV2: React.FC<IPhoneInputV2Props> = ({
    value,
    onChange: handlePhoneInputValueChange,
    defaultCountry,
    errorText = 'Please enter a valid phone number!',
    hideErrorText = false,
    placeholder = 'Phone number',
    label = '',
    lang = 'en',
    isValid = true,
    freezeCountryCode = false,
    usePhoneInputHookProps,
    flagImageAPIProps = {
        protocol: 'https',
        disableLazyLoading: false,
    },
    validation = {
        enable: false,
    },
    disableCountrySearch = false,
    countrySearchLabel = 'Search for your country',
    required = false,
    forceErrorText = false,
    hideErrorForCountryCode = true,
    ...restProps
}) => {
    const isRtl = document.dir === 'rtl';
    const _hideErrorForCountryCode = required ? !required : hideErrorForCountryCode;

    const [isValidPhoneNumber, setIsValidPhoneNumber] = useState<boolean | undefined>(undefined);
    const [search, setSearch] = useState('');
    const [countrySelectorTopPosition, setCountrySelectorTopPosition] = useState<number>(0);
    const [countrySelectorLeftPosition, setCountrySelectorLeftPosition] = useState<number>(0);
    const [countrySelectorWidth, setCountrySelectorWidth] = useState<number>(0);
    const [countrySelectorClicked, setCountrySelectorClicked] = useState<boolean>(false);
    const [didScreenResize, setDidScreenResize] = useState<boolean>(false);
    const [hasBlurredOnce, setHasBlurredOnce] = useState<boolean>(false);

    const countrySelectorRef = useRef<HTMLInputElement>(null); // ref for country selector dropdown
    const textFieldRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<any>(null);

    const styles = usePhoneInputV2Styles({ isCountrySearchEnabled: !disableCountrySearch });

    // get country name translations
    const [plainCountries, countries] = useMemo<[CountryData[], ParsedCountry[]]>(() => {
        if (lang === 'en') {
            return [defaultCountries, defaultCountries.map((o) => parseCountry(o))];
        }

        const translations = getCountryNameTranslations(lang);
        if (translations === undefined) {
            return [defaultCountries, defaultCountries.map((o) => parseCountry(o))];
        }

        const cc = cloneDeep(defaultCountries);
        return [
            cc,
            cc.map((country) => {
                const c = country[1] as keyof typeof translations;
                country[0] = translations[c];
                return parseCountry(country);
            }),
        ];
    }, [lang]);

    const filteredCountries = useMemo(() => {
        if (!search || disableCountrySearch) {
            return countries;
        }

        return countries.filter((o) => `${o.name.toLowerCase()} +${o.dialCode}`.includes(search.toLowerCase()));
    }, [countries, search]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const handleScreenResize = () => {
            setDidScreenResize((prev) => !prev);
        };

        window.addEventListener('resize', handleScreenResize);

        return () => {
            window.removeEventListener('resize', handleScreenResize);
        };
    }, []);

    useLayoutEffect(() => {
        if (countrySelectorRef.current) {
            const countrySelectorBoundingRect = countrySelectorRef.current.getBoundingClientRect();
            setCountrySelectorTopPosition(countrySelectorBoundingRect.bottom - 1);
        }
    }, [countrySelectorClicked, didScreenResize]);

    useLayoutEffect(() => {
        if (textFieldRef.current) {
            const textFieldBoundingRect = textFieldRef.current.getBoundingClientRect();
            setCountrySelectorWidth(textFieldBoundingRect.width - 1);
            setCountrySelectorLeftPosition(textFieldBoundingRect.left - 1);
        }
    }, [countrySelectorClicked, didScreenResize]);

    const callValidationAPI = useCallback((phoneNo: string, countryCode: string) => {
        if (!validation?.enable) {
            return;
        }

        AuthManager.getInstance()
            .validatePhone({
                phone: phoneNo,
                countryCode,
            })
            .then((res) => {
                setIsValidPhoneNumber(res?.valid === true);
                validation?.callbackFn?.(res?.valid === true);
            })
            .catch((err) => console.error('Error(phone-input): Unable to validate phone number', err));
    }, []);

    const debouncedInternalValidation = useCallback(
        debounce((inputVal: string, countryCode: string) => {
            callValidationAPI(inputVal, countryCode);
        }, validation?.debounceWait ?? 500),
        [],
    );

    const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
        defaultCountry: defaultCountry ?? 'us',
        value,
        countries: plainCountries,
        onChange: handlePhoneInputValueChange,
        forceDialCode: freezeCountryCode,
        charAfterDialCode: ' ',
        disableFormatting: true,
        ...usePhoneInputHookProps,
    });

    useEffect(() => {
        if (defaultCountry && defaultCountry !== country.iso2) {
            setCountry(defaultCountry as CountryIso2);
        }
    }, [defaultCountry]);

    const shouldShowErrorText = useMemo(() => {
        if (validation?.enable && forceErrorText) {
            return !isValidPhoneNumber;
        }

        if (!hasBlurredOnce) {
            return false;
        }

        if (!required && inputValue?.trim() === '') {
            return false;
        }

        if (validation?.enable) {
            if (
                _hideErrorForCountryCode &&
                inputValue &&
                inputValue?.substring(1).trim().replace(/\s/g, '') === country.dialCode
            ) {
                return false;
            }

            return !isValidPhoneNumber;
        }

        return !isValid;
    }, [isValidPhoneNumber, hideErrorText, inputValue, hasBlurredOnce, country, forceErrorText]);

    useEffect(() => {
        if (!validation?.enable) {
            return;
        }

        if (inputValue.substring(1) === country.dialCode || inputValue.trim().replace(/\s/g, '').length <= 6) {
            setIsValidPhoneNumber(false);
            validation?.callbackFn?.(false);
            return;
        }

        debounceRef.current = debouncedInternalValidation(inputValue, country.iso2);
    }, [inputValue]);

    const countrySelector = useMemo(() => {
        return (
            <InputAdornment
                position="start"
                style={{
                    marginRight: '8px',
                    marginLeft: '-8px',
                }}
                dir="ltr"
            >
                <Select
                    variant="outlined"
                    ref={countrySelectorRef}
                    value={country.iso2}
                    onChange={(e) => setCountry(e.target.value as CountryIso2)}
                    renderValue={(val) => (
                        <FlagImage iso2={val} style={styles.flagImageStyles} {...flagImageAPIProps} />
                    )}
                    disabled={freezeCountryCode}
                    disableUnderline
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                ...styles.menu,
                                top: `${countrySelectorTopPosition}px !important`,
                                width: `${countrySelectorWidth}px !important`,
                                left: `${countrySelectorLeftPosition}px !important`,
                            },
                        },
                    }}
                    sx={styles.select}
                    dir="ltr"
                    onMouseDown={() => {
                        // This will be used to calculate country selector positions(top & width) everytime user clicks on select.
                        // It will avoid country selector dropdown being misaligned when position changes due to validation error being shown
                        // or device orientation change.
                        setCountrySelectorClicked((prev) => !prev);
                    }}
                >
                    {!disableCountrySearch && (
                        <Box
                            onKeyDown={(e) => e.stopPropagation()}
                            sx={{
                                ...styles.countrySearch,
                                width: countrySelectorWidth,
                            }}
                        >
                            <TextField
                                label={countrySearchLabel}
                                fullWidth
                                size="small"
                                margin="dense"
                                value={search}
                                onChange={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSearch(e.target.value);
                                }}
                                sx={styles.countrySearchTextField}
                            />
                        </Box>
                    )}
                    {filteredCountries.map((c) => {
                        return (
                            <MenuItem key={c.iso2} value={c.iso2}>
                                <FlagImage iso2={c.iso2} style={{ margin: '0 8px' }} {...flagImageAPIProps} />
                                <Typography margin="0 8px">{c.name}</Typography>
                                <Typography color="gray">
                                    {isRtl ? '' : '+'}
                                    {c.dialCode}
                                    {isRtl ? '+' : ''}
                                </Typography>
                            </MenuItem>
                        );
                    })}
                </Select>
            </InputAdornment>
        );
    }, [
        country.iso2,
        isRtl,
        search,
        countrySelectorWidth,
        countrySelectorTopPosition,
        countrySelectorLeftPosition,
        countrySelectorClicked,
        lang,
    ]);

    return (
        <TextField
            variant="outlined"
            label={label}
            color="primary"
            placeholder={placeholder}
            value={inputValue}
            dir="ltr"
            onChange={(e) => {
                handlePhoneValueChange(e);
            }}
            onFocus={() => {
                // jump cursor to the end (-1 denotes the last index)
                inputRef.current?.setSelectionRange(-1, -1);
            }}
            onBlur={() => {
                debounceRef.current?.flush?.();
                setHasBlurredOnce(true);
            }}
            type="tel"
            ref={textFieldRef}
            inputRef={inputRef}
            InputProps={{
                startAdornment: countrySelector,
            }}
            sx={styles.textField}
            fullWidth
            error={shouldShowErrorText}
            helperText={
                <Collapse in={shouldShowErrorText} sx={styles.errorText}>
                    {errorText}
                </Collapse>
            }
            {...restProps}
        />
    );
};

export default PhoneInputV2;
